import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Spinner } from 'react-bootstrap';
import {
  isBlankValue,
  isMeasurableAchievedOnDay,
  toNumberOrNull
} from 'helpers/sicHelper';
import { resultColors } from './sicTableStyles';

const MANUAL_ENTER_INPUT_METHOD = 2;

const transparentInputStyle = {
  width: '100%',
  minWidth: '64px',
  border: 'none',
  background: 'transparent',
  color: 'inherit',
  fontWeight: 'inherit',
  textAlign: 'center',
  fontSize: '0.8rem',
  outline: 'none'
};

const yesNoSelectValue = value => {
  if (isBlankValue(value)) return '';
  return Number(value) === 1 ? '1' : '0';
};

const toDraftText = value => (isBlankValue(value) ? '' : String(value));

export default function SicValueCell({
  measurable,
  dayIndex,
  isSaving,
  onCommit,
  styles,
  rowBackground
}) {
  const value = measurable.values[dayIndex];
  const [draftText, setDraftText] = useState(toDraftText(value));

  useEffect(() => {
    setDraftText(toDraftText(value));
  }, [value]);

  const isEditable =
    measurable.inputMethod === MANUAL_ENTER_INPUT_METHOD && !isSaving;
  const colors = resultColors(
    styles,
    isMeasurableAchievedOnDay(measurable, dayIndex)
  );

  const commitDraft = () => {
    const draftNumber = toNumberOrNull(draftText);
    if (draftNumber === toNumberOrNull(value)) {
      setDraftText(toDraftText(value));
      return;
    }
    onCommit(draftNumber);
  };

  const renderEditor = () => {
    if (!isEditable) {
      if (isBlankValue(value)) return '';
      if (measurable.isYesNo) return Number(value) === 1 ? 'Yes' : 'No';
      return value;
    }

    if (measurable.isYesNo) {
      return (
        <select
          value={yesNoSelectValue(value)}
          onChange={e =>
            onCommit(e.target.value === '' ? null : Number(e.target.value))
          }
          style={{ ...transparentInputStyle, cursor: 'pointer' }}
        >
          <option value="">-</option>
          <option value="1">Yes</option>
          <option value="0">No</option>
        </select>
      );
    }

    return (
      <input
        type="number"
        step="any"
        value={draftText}
        onChange={e => setDraftText(e.target.value)}
        onBlur={commitDraft}
        onKeyDown={e => e.key === 'Enter' && e.currentTarget.blur()}
        style={transparentInputStyle}
      />
    );
  };

  return (
    <td
      style={{
        ...styles.bodyCell,
        backgroundColor: rowBackground,
        ...colors,
        fontWeight: Object.keys(colors).length ? 600 : 'normal',
        padding: '2px 4px',
        textAlign: 'center'
      }}
    >
      {isSaving ? <Spinner animation="border" size="sm" /> : renderEditor()}
    </td>
  );
}

SicValueCell.propTypes = {
  measurable: PropTypes.object.isRequired,
  dayIndex: PropTypes.number.isRequired,
  isSaving: PropTypes.bool,
  onCommit: PropTypes.func.isRequired,
  styles: PropTypes.object.isRequired,
  rowBackground: PropTypes.string
};
